import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Papa from "papaparse";
import type { BatchItem, BatchItemResult } from "shared";
import { importLinks } from "@/api/links";
import { ApiException } from "@/api/client";
import { copyText, shortUrl } from "@/lib/format";

/** Must match server `MAX_BATCH` (routes/links.ts). Server validates anyway. */
const MAX_BATCH = 100;

type Mode = "paste" | "csv";

function parsePaste(text: string): BatchItem[] {
	return text
		.split(/\r?\n/)
		.map((l) => l.trim())
		.filter((l) => l.length > 0)
		.map((l) => ({ targetUrl: l }));
}

/** Parse CSV with header row: targetUrl, hash?, startsAt?, expiresAt? */
function parseCsv(text: string): { items: BatchItem[]; error?: string } {
	const result = Papa.parse<Record<string, string>>(text, {
		header: true,
		skipEmptyLines: true,
		transformHeader: (h) => h.trim().toLowerCase(),
	});
	const rows = (result.data ?? []).filter(
		(r) => r && typeof r === "object",
	);
	const items: BatchItem[] = [];
	for (const r of rows) {
		const targetUrl = (r.targeturl ?? r.target_url ?? r.url ?? "").trim();
		if (!targetUrl) continue;
		const item: BatchItem = { targetUrl };
		const hash = (r.hash ?? "").trim();
		if (hash) item.hash = hash;
		const startsAt = (r.startsat ?? r.starts_at ?? "").trim();
		if (startsAt) item.startsAt = startsAt;
		const expiresAt = (r.expiresat ?? r.expires_at ?? "").trim();
		if (expiresAt) item.expiresAt = expiresAt;
		items.push(item);
	}
	return { items };
}

export function LinkImportPage() {
	const [mode, setMode] = useState<Mode>("paste");
	const [paste, setPaste] = useState("");
	const [csvName, setCsvName] = useState("");
	const [csvItems, setCsvItems] = useState<BatchItem[]>([]);
	const [csvError, setCsvError] = useState<string | null>(null);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [results, setResults] = useState<BatchItemResult[] | null>(null);
	const [summary, setSummary] = useState<{
		created: number;
		reassigned: number;
		failed: number;
	} | null>(null);
	const [copiedIdx, setCopiedIdx] = useState<number | null>(null);

	const items = useMemo(
		() => (mode === "paste" ? parsePaste(paste) : csvItems),
		[mode, paste, csvItems],
	);
	const overCap = items.length > MAX_BATCH;

	async function onFile(file: File) {
		setCsvName(file.name);
		setCsvError(null);
		try {
			const text = await file.text();
			const { items: parsed, error: err } = parseCsv(text);
			if (err) {
				setCsvError(err);
				setCsvItems([]);
				return;
			}
			setCsvItems(parsed);
		} catch {
			setCsvError("Could not read file");
			setCsvItems([]);
		}
	}

	async function onSubmit() {
		const batch = items.slice(0, MAX_BATCH);
		if (batch.length === 0) return;
		setBusy(true);
		setError(null);
		setResults(null);
		setSummary(null);
		try {
			const res = await importLinks(batch);
			setResults(res.results);
			setSummary({
				created: res.created,
				reassigned: res.reassigned,
				failed: res.failed,
			});
		} catch (err) {
			setError(
				err instanceof ApiException
					? err.message
					: "Import failed",
			);
		} finally {
			setBusy(false);
		}
	}

	async function onCopy(idx: number, hash: string) {
		const ok = await copyText(shortUrl(hash));
		if (ok) {
			setCopiedIdx(idx);
			setTimeout(() => setCopiedIdx(null), 1500);
		}
	}

	return (
		<div className="space-y-6">
			<div>
				<p className="text-sm text-fg-muted">
					<Link to="/links" className="hover:underline">
						Links
					</Link>{" "}
					/ Import
				</p>
				<h1 className="mt-1 text-xl font-semibold tracking-tight">
					Batch import
				</h1>
				<p className="mt-1 text-sm text-fg-muted">
					Create many links at once. Collisions reuse the reassign
					rules (live → row fails, dead → reassign, history kept).
				</p>
			</div>

			<div className="flex gap-2">
				<button
					type="button"
					className={`rounded-lg px-3 py-1.5 text-sm ${mode === "paste" ? "bg-teal-500 text-white" : "border border-border hover:bg-surface-raised"}`}
					onClick={() => setMode("paste")}
				>
					Paste URLs
				</button>
				<button
					type="button"
					className={`rounded-lg px-3 py-1.5 text-sm ${mode === "csv" ? "bg-teal-500 text-white" : "border border-border hover:bg-surface-raised"}`}
					onClick={() => setMode("csv")}
				>
					CSV upload
				</button>
			</div>

			{mode === "paste" ? (
				<div className="space-y-2">
					<label className="block text-sm">
						<span className="text-fg-muted">
							One URL per line (auto-generated hashes)
						</span>
						<textarea
							value={paste}
							onChange={(e) => setPaste(e.target.value)}
							rows={10}
							placeholder={"https://example.com/one\nhttps://example.com/two"}
							className="mt-1 w-full rounded-lg border border-border bg-surface px-3 py-2 font-mono text-sm outline-none focus:ring-2 focus:ring-accent/40"
						/>
					</label>
				</div>
			) : (
				<div className="space-y-2">
					<label className="block text-sm">
						<span className="text-fg-muted">
							CSV with header row:{" "}
							<code className="font-mono">
								targetUrl,hash,startsAt,expiresAt
							</code>{" "}
							(hash + dates optional)
						</span>
						<input
							type="file"
							accept=".csv,text/csv"
							className="mt-1 block w-full text-sm text-fg-muted file:mr-3 file:rounded-lg file:border-0 file:bg-teal-500 file:px-3 file:py-2 file:text-sm file:font-medium file:text-white hover:file:bg-teal-600"
							onChange={(e) => {
								const f = e.target.files?.[0];
								if (f) void onFile(f);
							}}
						/>
					</label>
					{csvName && (
						<p className="text-xs text-fg-muted">
							Loaded <span className="font-mono">{csvName}</span> →{" "}
							{csvItems.length} rows
						</p>
					)}
					{csvError && (
						<p className="text-xs text-rose-600 dark:text-rose-400">
							{csvError}
						</p>
					)}
				</div>
			)}

			{items.length > 0 && (
				<div className="space-y-2">
					<div className="flex flex-wrap items-center justify-between gap-2">
						<p className="text-sm text-fg-muted">
							Preview:{" "}
							<span className="font-mono">{items.length}</span>{" "}
							row{items.length === 1 ? "" : "s"}
						</p>
						<button
							type="button"
							disabled={busy || overCap || items.length === 0}
							onClick={() => void onSubmit()}
							className="rounded-lg bg-teal-500 px-3 py-2 text-sm font-medium text-white hover:bg-teal-600 disabled:opacity-50"
						>
							{busy ? "Importing…" : `Import ${Math.min(items.length, MAX_BATCH)} link${items.length === 1 ? "" : "s"}`}
						</button>
					</div>
					{overCap && (
						<p className="text-xs text-rose-600 dark:text-rose-400">
							Too many rows — only the first {MAX_BATCH} will be
							imported.
						</p>
					)}
					<div className="overflow-x-auto rounded-xl border border-border">
						<table className="w-full min-w-[520px] text-left text-sm">
							<thead className="border-b border-border bg-surface-raised text-fg-muted">
								<tr>
									<th className="px-3 py-2 font-medium">#</th>
									<th className="px-3 py-2 font-medium">Target</th>
									<th className="px-3 py-2 font-medium">Hash</th>
									<th className="px-3 py-2 font-medium">Window</th>
								</tr>
							</thead>
							<tbody>
								{items.slice(0, MAX_BATCH).map((it, i) => (
									<tr
										key={i}
										className="border-b border-border last:border-0"
									>
										<td className="px-3 py-2 text-fg-muted">
											{i + 1}
										</td>
										<td className="max-w-[16rem] truncate px-3 py-2 text-fg-muted">
											{it.targetUrl}
										</td>
										<td className="px-3 py-2 font-mono text-xs">
											{it.hash ?? "auto"}
										</td>
										<td className="px-3 py-2 text-xs text-fg-muted">
											{[it.startsAt, it.expiresAt]
												.filter(Boolean)
												.join(" → ") || "—"}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</div>
			)}

			{error && (
				<div className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-700 dark:text-rose-300">
					{error}
				</div>
			)}

			{summary && (
				<div className="space-y-3">
					<div className="flex flex-wrap gap-3 text-sm">
						<span className="rounded-full bg-emerald-500/15 px-3 py-1 font-medium text-emerald-700 dark:text-emerald-400">
							{summary.created} created
						</span>
						<span className="rounded-full bg-amber-500/15 px-3 py-1 font-medium text-amber-700 dark:text-amber-400">
							{summary.reassigned} reassigned
						</span>
						<span className="rounded-full bg-rose-500/15 px-3 py-1 font-medium text-rose-700 dark:text-rose-400">
							{summary.failed} failed
						</span>
					</div>

					{results && results.length > 0 && (
						<div className="overflow-x-auto rounded-xl border border-border">
							<table className="w-full min-w-[520px] text-left text-sm">
								<thead className="border-b border-border bg-surface-raised text-fg-muted">
									<tr>
										<th className="px-3 py-2 font-medium">#</th>
										<th className="px-3 py-2 font-medium">Result</th>
										<th className="px-3 py-2 font-medium">Link</th>
										<th className="px-3 py-2 font-medium">Detail</th>
									</tr>
								</thead>
								<tbody>
									{results.map((r) => (
										<tr
											key={r.index}
											className="border-b border-border last:border-0"
										>
											<td className="px-3 py-2 text-fg-muted">
												{r.index + 1}
											</td>
											<td className="px-3 py-2">
												{r.ok ? (
													<span className="text-emerald-700 dark:text-emerald-400">
														{r.reassigned
															? "reassigned"
															: "created"}
													</span>
												) : (
													<span className="text-rose-600 dark:text-rose-400">
														failed
													</span>
												)}
											</td>
											<td className="px-3 py-2 font-mono text-xs">
												{r.ok && r.link ? (
													<button
														type="button"
														className="text-accent hover:underline"
														onClick={() =>
															void onCopy(
																r.index,
																r.link!.hash,
															)
														}
													>
														/{r.link.hash}
														{copiedIdx === r.index && (
															<span className="ml-1 text-fg-muted">
																copied
															</span>
														)}
													</button>
												) : (
													"—"
												)}
											</td>
											<td className="px-3 py-2 text-xs text-fg-muted">
												{r.ok
													? (r.link && (
															<Link
																to={`/links/${r.link.id}`}
																className="text-accent hover:underline"
															>
																Open
															</Link>
														))
													: (r.message ?? r.code ?? "error")}
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					)}
				</div>
			)}
		</div>
	);
}
